import React from 'react';

// Official state sex offender registry names
const STATE_REGISTRIES = {
  AL: { name: 'Alabama', registry: 'Alabama Law Enforcement Agency' },
  AK: { name: 'Alaska', registry: 'Alaska Dept. of Public Safety' },
  AZ: { name: 'Arizona', registry: 'Arizona Dept. of Public Safety' },
  AR: { name: 'Arkansas', registry: 'Arkansas Sex Offender Registry' },
  CA: { name: 'California', registry: 'CA Megan\'s Law' },
  CO: { name: 'Colorado', registry: 'Colorado Bureau of Investigation' },
  CT: { name: 'Connecticut', registry: 'CT Sex Offender Registry' },
  DE: { name: 'Delaware', registry: 'Delaware State Police' },
  DC: { name: 'Washington DC', registry: 'DC Sex Offender Registry' },
  FL: { name: 'Florida', registry: 'Florida Dept. of Law Enforcement' },
  GA: { name: 'Georgia', registry: 'Georgia Bureau of Investigation' },
  HI: { name: 'Hawaii', registry: 'Hawaii Criminal Justice Data Center' },
  ID: { name: 'Idaho', registry: 'Idaho State Police' },
  IL: { name: 'Illinois', registry: 'Illinois State Police' },
  IN: { name: 'Indiana', registry: 'Indiana Sheriff\'s Sex Offender Registry' },
  IA: { name: 'Iowa', registry: 'Iowa Sex Offender Registry' },
  KS: { name: 'Kansas', registry: 'Kansas Bureau of Investigation' },
  KY: { name: 'Kentucky', registry: 'Kentucky State Police' },
  LA: { name: 'Louisiana', registry: 'Louisiana State Police' },
  ME: { name: 'Maine', registry: 'Maine Sex Offender Registry' },
  MD: { name: 'Maryland', registry: 'Maryland Dept. of Public Safety' },
  MA: { name: 'Massachusetts', registry: 'MA Sex Offender Registry Board' },
  MI: { name: 'Michigan', registry: 'Michigan State Police' },
  MN: { name: 'Minnesota', registry: 'MN Bureau of Criminal Apprehension' },
  MS: { name: 'Mississippi', registry: 'Mississippi Sex Offender Registry' },
  MO: { name: 'Missouri', registry: 'Missouri State Highway Patrol' },
  MT: { name: 'Montana', registry: 'Montana Dept. of Justice' },
  NE: { name: 'Nebraska', registry: 'Nebraska State Patrol' },
  NV: { name: 'Nevada', registry: 'Nevada Sex Offender Registry' },
  NH: { name: 'New Hampshire', registry: 'NH Dept. of Safety' },
  NJ: { name: 'New Jersey', registry: 'NJ Megan\'s Law Registry' },
  NM: { name: 'New Mexico', registry: 'NM Dept. of Public Safety' },
  NY: { name: 'New York', registry: 'NY Division of Criminal Justice Services' },
  NC: { name: 'North Carolina', registry: 'NC Sex Offender Registry' },
  ND: { name: 'North Dakota', registry: 'ND Attorney General' },
  OH: { name: 'Ohio', registry: 'Ohio Attorney General eSORN' },
  OK: { name: 'Oklahoma', registry: 'OK Dept. of Corrections' },
  OR: { name: 'Oregon', registry: 'Oregon State Police' },
  PA: { name: 'Pennsylvania', registry: 'PA Megan\'s Law' },
  RI: { name: 'Rhode Island', registry: 'RI Sex Offender Registry' },
  SC: { name: 'South Carolina', registry: 'SC Sex Offender Registry' },
  SD: { name: 'South Dakota', registry: 'SD Division of Criminal Investigation' },
  TN: { name: 'Tennessee', registry: 'TN Bureau of Investigation' },
  TX: { name: 'Texas', registry: 'Texas Dept. of Public Safety' },
  UT: { name: 'Utah', registry: 'Utah Sex Offender Registry' },
  VT: { name: 'Vermont', registry: 'VT Sex Offender Registry' },
  VA: { name: 'Virginia', registry: 'Virginia State Police' },
  WA: { name: 'Washington', registry: 'WA Assoc. of Sheriffs & Police Chiefs' },
  WV: { name: 'West Virginia', registry: 'WV State Police' },
  WI: { name: 'Wisconsin', registry: 'WI Dept. of Corrections' },
  WY: { name: 'Wyoming', registry: 'WY Division of Criminal Investigation' },
};

/**
 * Compact inline badge for search result cards.
 * Shows state abbreviation + shield icon.
 */
export function StateSourceBadge({ stateCode, className = '' }) {
  if (!stateCode) return null;
  const info = STATE_REGISTRIES[stateCode.toUpperCase()];
  const label = info ? info.name : stateCode;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-900/60 text-blue-300 border border-blue-700/40 ${className}`}
      title={info ? `Source: ${info.registry}` : `Source: ${stateCode} Registry`}
    >
      <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
      {label}
    </span>
  );
}

/**
 * Full source badge for the detail page.
 * Shows official registry name with verified styling.
 */
export function StateSourceFull({ stateCode, className = '' }) {
  if (!stateCode) return null;
  const info = STATE_REGISTRIES[stateCode.toUpperCase()];

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-900/40 border border-blue-700/30 ${className}`}>
      <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-blue-400/70 font-semibold leading-none">Verified Source</div>
        <div className="text-xs text-blue-200 font-medium truncate">
          {info ? info.registry : `${stateCode} Sex Offender Registry`}
        </div>
      </div>
    </div>
  );
}

export { STATE_REGISTRIES };
